import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle, CheckCircle2, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { COMPANY, IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", company: "", email: "", phone: "", country: "", requirement: "", message: "" };

const fields = [
  { name: "name", label: "Name", type: "text", required: true, half: true },
  { name: "company", label: "Company Name", type: "text", half: true },
  { name: "email", label: "Email", type: "email", required: true, half: true },
  { name: "phone", label: "Phone / WhatsApp", type: "text", half: true },
  { name: "country", label: "Country", type: "text", half: true },
  { name: "requirement", label: "Product / Requirement", type: "text", half: true },
];

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-[color:var(--brand-blue)]/20";

  const contactItems = [
    { icon: MapPin, label: "Address", value: COMPANY.address, href: null },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.emailRaw}`, testid: "contact-email" },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contact Us"
        title="Let's discuss your sourcing and trade requirement."
        subtitle="Send us an enquiry and our team will get back to you shortly."
        image={IMAGES.cargoShip}
      />

      <section className="bg-white py-24 lg:py-32" data-testid="contact-section">
        <div className="container-x grid gap-12 lg:grid-cols-[1.3fr_1fr] lg:gap-16">
          {/* Form */}
          <Reveal>
            <div className="rounded-3xl border border-slate-200 bg-[#fafafa] p-8 lg:p-10">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="thanks"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                    data-testid="enquiry-success"
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--brand-blue)]/10 text-[color:var(--brand-blue)]">
                      <CheckCircle2 className="h-9 w-9" />
                    </div>
                    <h2 className="mt-6 text-2xl font-bold text-slate-950">Thank you for contacting us</h2>
                    <p className="mt-3 max-w-md text-slate-600">
                      Our team will get back to you shortly. We appreciate your interest in {COMPANY.name}.
                    </p>
                    <button
                      onClick={() => setDone(false)}
                      className="mt-8 rounded-full border border-slate-300 px-6 py-2.5 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:bg-white"
                      data-testid="send-another"
                    >
                      Send another enquiry
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={submit}
                    noValidate
                    className="space-y-5"
                    data-testid="enquiry-form"
                  >
                    <div>
                      <h2 className="text-2xl font-bold text-slate-950">Send an Enquiry</h2>
                      <p className="mt-1.5 text-sm text-slate-600">Fields marked with * are required.</p>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      {fields.map((f) => (
                        <div key={f.name} className={f.half ? "" : "sm:col-span-2"}>
                          <label className="mb-1.5 block text-sm font-medium text-slate-700">
                            {f.label}{f.required && <span className="text-[color:var(--brand-red)]"> *</span>}
                          </label>
                          <input
                            type={f.type}
                            name={f.name}
                            value={form[f.name]}
                            onChange={update}
                            required={f.required}
                            className={inputCls}
                            data-testid={`field-${f.name}`}
                          />
                        </div>
                      ))}
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        Message<span className="text-[color:var(--brand-red)]"> *</span>
                      </label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={update}
                        rows={5}
                        required
                        className={inputCls}
                        data-testid="field-message"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[color:var(--brand-red)] px-7 py-3.5 text-base font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70 sm:w-auto"
                      data-testid="submit-enquiry-button"
                    >
                      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                      {loading ? "Submitting..." : "Submit Enquiry"}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>

          {/* Direct contact */}
          <Reveal delay={0.1}>
            <div className="space-y-4">
              <div className="rounded-3xl border border-slate-200 bg-white p-8">
                <span className="eyebrow text-[color:var(--brand-blue)]">Direct Contact</span>
                <h3 className="mt-3 text-xl font-bold text-slate-950">Reach us directly</h3>
                <ul className="mt-6 space-y-5">
                  {contactItems.map((c) => (
                    <li key={c.label} className="flex items-start gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[color:var(--brand-blue)]/10 text-[color:var(--brand-blue)]">
                        <c.icon className="h-5 w-5" strokeWidth={1.7} />
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
                        {c.href ? (
                          <a href={c.href} target={c.label === "WhatsApp" ? "_blank" : undefined} rel="noreferrer" className="text-sm font-medium text-slate-900 hover:text-[color:var(--brand-blue)]" data-testid={c.testid}>
                            {c.value}
                          </a>
                        ) : (
                          <p className="text-sm font-medium text-slate-900">{c.value}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200">
                <iframe
                  title="Location"
                  src="https://www.google.com/maps?q=Glenealy+Tower,+1+Glenealy,+Central,+Hong+Kong&output=embed"
                  className="h-64 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
